import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  NumberInput,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import {
  IconBuildingHospital,
  IconPlus,
  IconSearch,
  IconTrash,
} from "@/lib/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import {
  createOrganization,
  deleteOrganization,
  listOrganizations,
  type OrganizationCreatePayload,
} from "@/lib/api/references_full";
import { useAuthStore } from "@/stores/auth";

export function OrganizationsPage() {
  const { t } = useTranslation();
  const canEdit = useAuthStore((s) => s.hasPermission("reference.update"));
  const [q, setQ] = useState("");
  const [debQ] = useDebouncedValue(q, 300);
  const [opened, { open, close }] = useDisclosure(false);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["organizations", debQ],
    queryFn: () => listOrganizations({ q: debQ || undefined, limit: 100 }),
  });

  const form = useForm<OrganizationCreatePayload>({
    initialValues: {
      name: "",
      legal_name: "",
      inn: "",
      address: "",
      contact_name: "",
      contact_phone: "",
      contact_email: "",
      discount_percent: 0,
      notes: "",
    },
  });

  const createMutation = useMutation({
    mutationFn: createOrganization,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["organizations"] });
      notifications.show({ color: "green", message: t("references.org.created") });
      form.reset();
      close();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteOrganization,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["organizations"] });
      notifications.show({ color: "green", message: t("references.org.deactivated") });
    },
  });

  const onSubmit = form.onSubmit((vals) => {
    createMutation.mutate({
      ...vals,
      legal_name: vals.legal_name || null,
      inn: vals.inn || null,
      address: vals.address || null,
      contact_name: vals.contact_name || null,
      contact_phone: vals.contact_phone || null,
      contact_email: vals.contact_email || null,
      notes: vals.notes || null,
    });
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title={t("references.organizations")}
        subtitle={data ? `${data.total} ${t("references.org.name")}` : undefined}
        back
        actions={
          canEdit && (
            <Button
              leftSection={<IconPlus size={16} strokeWidth={1.8} />}
              onClick={open}
              radius="xl"
            >
              {t("references.org.create")}
            </Button>
          )
        }
      />

      <Card padding="md">
        <TextInput
          rightSection={<IconSearch size={18} strokeWidth={1.8} />}
          placeholder={t("references.org.search")}
          value={q}
          onChange={(e) => setQ(e.currentTarget.value)}
        />
      </Card>

      <Card padding={0}>
        <Table.ScrollContainer minWidth={760}>
          <Table striped highlightOnHover verticalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("references.org.name")}</Table.Th>
                <Table.Th>{t("references.org.inn")}</Table.Th>
                <Table.Th>{t("references.org.contact")}</Table.Th>
                <Table.Th>{t("references.org.discount")}</Table.Th>
                <Table.Th>{t("users.fields.status")}</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text c="dimmed" ta="center" py="md">
                      {t("common.loading")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {data?.items.length === 0 && !isLoading && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <EmptyState
                      icon={<IconBuildingHospital size={32} strokeWidth={1.5} />}
                      title={t("references.org.empty")}
                    />
                  </Table.Td>
                </Table.Tr>
              )}
              {data?.items.map((o) => (
                <Table.Tr key={o.id}>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text fw={600} size="sm">
                        {o.name}
                      </Text>
                      {o.legal_name && (
                        <Text c="dimmed" size="xs">
                          {o.legal_name}
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Text ff="monospace" size="sm">
                      {o.inn ?? "—"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text size="sm">{o.contact_name ?? "—"}</Text>
                      {o.contact_phone && (
                        <Text c="dimmed" size="xs" ff="monospace">
                          {o.contact_phone}
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="blue" radius="sm">
                      {o.discount_percent}%
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge
                      status={o.is_active ? "active" : "inactive"}
                      i18nPrefix="users"
                    />
                  </Table.Td>
                  <Table.Td>
                    {canEdit && o.is_active && (
                      <Tooltip label={t("references.org.deactivate")}>
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => deleteMutation.mutate(o.id)}
                        >
                          <IconTrash size={16} strokeWidth={1.8} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <Modal opened={opened} onClose={close} title={t("references.org.create")} size="md">
        <form onSubmit={onSubmit}>
          <Stack>
            <TextInput
              label={t("references.org.name")}
              placeholder={t("placeholder.name")}
              required
              {...form.getInputProps("name")}
            />
            <TextInput
              label={t("references.org.legal_name")}
              placeholder={t("placeholder.legal_name")}
              {...form.getInputProps("legal_name")}
            />
            <TextInput
              label={t("references.org.inn")}
              placeholder={t("placeholder.inn")}
              {...form.getInputProps("inn")}
            />
            <TextInput
              label={t("references.org.address")}
              placeholder={t("placeholder.address")}
              {...form.getInputProps("address")}
            />
            <Group grow>
              <TextInput
                label={t("references.org.contact_name")}
                placeholder={t("placeholder.family_name")}
                {...form.getInputProps("contact_name")}
              />
              <TextInput
                label={t("references.org.contact_phone")}
                placeholder={t("placeholder.phone")}
                {...form.getInputProps("contact_phone")}
              />
            </Group>
            <TextInput
              label={t("references.org.contact_email")}
              placeholder={t("placeholder.email")}
              type="email"
              {...form.getInputProps("contact_email")}
            />
            <NumberInput
              label={t("references.org.discount")}
              min={0}
              max={100}
              suffix="%"
              {...form.getInputProps("discount_percent")}
            />
            <Textarea
              label={t("references.org.notes")}
              placeholder={t("placeholder.notes")}
              {...form.getInputProps("notes")}
            />
            <Group justify="flex-end" mt="sm">
              <Button variant="subtle" color="gray" onClick={close}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={createMutation.isPending}>
                {t("common.create")}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
